import {
  alpha,
  Card,
  CardContent,
  Chip,
  Grid2 as Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import {
  AssignmentOutlined,
  OpenInNew,
  WarningAmber
} from '@mui/icons-material'
import { getECGResultConfig } from 'pages/Diagnostics/constants'
import { useDiagnosisAgreement } from '../../../context/DiagnosisAgreementContext'
import { DisagreeButton } from '../../common/DisagreeButton'
import { useToast } from 'services/context/toast'
import { IECGDiagnostics } from 'pages/Diagnostics/types'
import { IExamResultResponse } from 'types/exam/types'
import {
  INSIGHT_LABELS,
  INSIGHT_COLORS,
  SBC_PROTOCOL_URL
} from '../constants/diagnostics'
import { resultDisclaimer } from '../../common/disclaimer_text'

interface ECGDiagnosticCardProps {
  result: IExamResultResponse
  diagnosisData: IECGDiagnostics
  onCardClick: (leads: string[]) => void
  isEmergency?: boolean
  variant?: 'normal' | 'pathological' | 'emergency'
}

export const ECGDiagnosticCard = ({
  result,
  diagnosisData,
  onCardClick,
  isEmergency = false,
  variant = 'pathological'
}: ECGDiagnosticCardProps) => {
  const { agreements, handleAgreement, isLoading } = useDiagnosisAgreement()
  const toast = useToast()
  const theme = useTheme()
  const resultConfig = getECGResultConfig(result.result_value)

  const handleDisagreeClick = async () => {
    await handleAgreement({ ...result, key: result.result_type })
  }

  const handleDisagreeSuccess = () => {
    toast.handler({
      content: 'Obrigado! Seu feedback ajudará a melhorar o modelo de análise',
      severity: 'success',
      duration: 5000
    })
  }

  const getInsightLabel = (insight: string) =>
    INSIGHT_LABELS[insight] || insight
  const getInsightColor = (insight: string) =>
    INSIGHT_COLORS[insight] || 'default'

  // Determinar a cor da borda baseado no variant
  const getBorderColor = () => {
    if (variant === 'emergency') return theme.palette.error.main
    if (variant === 'normal') return theme.palette.success.main
    return alpha(theme.palette.primary.main, 0.04)
  }

  const getBackgroundColor = () => {
    if (variant === 'normal') return alpha(theme.palette.success.main, 0.04)
    return alpha(theme.palette.primary.main, 0.04)
  }

  return (
    <Card
      sx={{
        height: '100%',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        border: `2px solid ${getBorderColor()}`,
        backgroundColor: getBackgroundColor(),
        '&:hover': {
          border: theme => `2px solid ${theme.palette.text.primary}`
        }
      }}
      onClick={() => onCardClick(diagnosisData.leads)}
    >
      <CardContent sx={{ py: 2, height: '100%' }}>
        <Grid
          container
          spacing={2}
          justifyContent='space-between'
          sx={{
            height: '100%'
          }}
        >
          {/* Left Section - Diagnosis Info */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography
              variant='subtitle1'
              sx={{
                fontWeight: 'bold',
                mb: 1,
                color: 'var(--primarySoft-button-color)'
              }}
            >
              {diagnosisData.name}
            </Typography>

            <Typography variant='body2' sx={{ mb: 1.5, ml: 1 }}>
              <strong>Descrição:</strong> {diagnosisData.description}
            </Typography>

            <Typography
              variant='body2'
              sx={{
                mb: 1,
                ml: 1,
                display: 'flex',
                flexWrap: 'wrap'
              }}
            >
              <strong>Derivações afetadas:</strong>
              <span style={{ marginLeft: '8px' }}>
                {diagnosisData.leads.join(', ')}
              </span>
            </Typography>

            <Typography
              variant='body2'
              component='div'
              sx={{
                ml: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap'
              }}
            >
              <strong>Classificação:</strong>
              {diagnosisData.insight.map(insight => (
                <Chip
                  key={insight}
                  label={getInsightLabel(insight)}
                  size='small'
                  color={getInsightColor(insight)}
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Typography>

            <Typography
              variant='body2'
              sx={{
                mt: 1.5,
                ml: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: resultConfig.color,
                fontWeight: 'medium'
              }}
            >
              <strong>Análise:</strong>
              {resultConfig.label}
            </Typography>

            <Grid
              mt={2}
              display='flex'
              justifyContent='center'
              sx={{ width: '150px' }}
            >
              <Link
                href={SBC_PROTOCOL_URL}
                target='_blank'
                underline='hover'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <AssignmentOutlined fontSize='small' />
                Protocolo SBC
                <OpenInNew fontSize='small' sx={{ fontSize: '0.875rem' }} />
              </Link>
            </Grid>
          </Grid>

          {/* Right Section - Actions */}
          <Grid
            size={{ xs: 12, md: 4 }}
            container
            direction='column'
            alignItems={{ xs: 'flex-start', md: 'flex-end' }}
            justifyContent='space-between'
            spacing={1}
          >
            <Grid>
              <DisagreeButton
                isDisagreed={agreements[result.result_type] === 'disagree'}
                onClick={handleDisagreeClick}
                disabled={false}
                loading={isLoading[result.result_type]}
                onSuccess={handleDisagreeSuccess}
                sx={{ width: '110px', padding: '5px' }}
              />
            </Grid>
            <Grid textAlign='right'>
              <Tooltip
                title={resultDisclaimer}
                placement='top-start'
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        options: { padding: 8 }
                      },
                      {
                        name: 'flip',
                        enabled: true
                      }
                    ],
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        maxWidth: 400,
                        padding: '8px 12px',
                        backgroundColor: theme.palette.warning.main,
                        color: theme.palette.warning.contrastText,
                        fontSize: '0.7rem'
                      }
                    }
                  }
                }}
              >
                <IconButton
                  size='small'
                  sx={{
                    color: 'warning.main',
                    alignSelf: 'flex-start'
                  }}
                >
                  <WarningAmber />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
